import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "./App.css";
import "./styles/theme-colors.css";
import "./styles/theme-independence-day.css";
import "./styles/theme-book-lovers-day.css";
import "./styles/theme-september-literacy.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Pages
import HomePage from "./pages/HomePage";
import BrowsePage from "./pages/BrowsePage";
import BookDetailPage from "./pages/BookDetailPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ReflectionsPage from "./pages/ReflectionsPage";
import FocusBigIdeaPage from "./pages/FocusBigIdeaPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set global axios timeout so requests don't hang forever
axios.defaults.timeout = 15000;

// Admin Auth Context
const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
};

const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));

  const checkAdmin = useCallback(async () => {
    if (!adminToken) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        withCredentials: true
      });
      setIsAdmin(true);
    } catch (error) {
      setIsAdmin(false);
      localStorage.removeItem('admin_token');
      setAdminToken(null);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    checkAdmin();
  }, [checkAdmin]);

  const login = async (password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      const token = response.data.token;
      localStorage.setItem('admin_token', token);
      setAdminToken(token);
      setIsAdmin(true);
      return true;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
        withCredentials: true
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem('admin_token');
      setAdminToken(null);
      setIsAdmin(false);
    }
  };

  const value = {
    isAdmin,
    loading,
    adminToken,
    login,
    logout,
    checkAdmin
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

// Protected Route for Admin
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAdmin();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--highlight)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  // Seasonal theme toggle — set to true for Independence Day, false to revert
  const INDEPENDENCE_DAY_THEME = false;
  // Book Lovers Day — August 9th only
  const BOOK_LOVERS_DAY_THEME = false;
  // September — International Literacy Day
  const SEPTEMBER_LITERACY_THEME = false;

  useEffect(() => {
    if (INDEPENDENCE_DAY_THEME) {
      document.body.classList.add('theme-independence-day');
    } else {
      document.body.classList.remove('theme-independence-day');
    }
    if (BOOK_LOVERS_DAY_THEME) {
      document.body.classList.add('theme-book-lovers-day');
    } else {
      document.body.classList.remove('theme-book-lovers-day');
    }
    if (SEPTEMBER_LITERACY_THEME) {
      document.body.classList.add('theme-september');
    } else {
      document.body.classList.remove('theme-september');
    }
    return () => {
      document.body.classList.remove('theme-independence-day');
      document.body.classList.remove('theme-book-lovers-day');
      document.body.classList.remove('theme-september');
    };
  }, [INDEPENDENCE_DAY_THEME, BOOK_LOVERS_DAY_THEME, SEPTEMBER_LITERACY_THEME]);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <AdminProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/browse" element={<BrowsePage />} />
          <Route path="/book/:bookId" element={<BookDetailPage />} />
          <Route path="/book" element={<Navigate to="/browse" replace />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/reflections" element={<ReflectionsPage />} />
          <Route path="/reflection/focus-big-idea" element={<FocusBigIdeaPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster 
          position="top-right" 
          richColors
          toastOptions={{
            style: {
              background: '#FDFBF7',
              border: '1px solid #D9C9AD',
              color: '#4E3E2B'
            }
          }}
        />
      </AdminProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
export { API, BACKEND_URL };
